/**
 * Find revision lines (calibre lineage design memo §5.8, Phase 5).
 *
 * Runs detectRevisionClusters over the manufacturer's calibres and lists the
 * candidate clusters with their evidence. Clusters that look like tiers or
 * peers, and stems the curator dismissed, are hidden by default.
 *
 * Per cluster: Create line (one click: the line plus each unlined member, with
 * its suffix as the revision label — no years, no current flag), Ask AI (the
 * LineageSuggestionDialog in cluster mode), or Not a revision (persisted on the
 * manufacturer, so the cluster stays hidden).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import {
  dismissRevisionStem,
  getCalibresForManufacturer,
  getDismissedRevisionStems,
  saveLineage,
  setCalibreRevision,
} from '../../services/calibreLineage';
import {
  detectRevisionClusters,
  foldedRevisionCodes,
  isDismissedCluster,
  type RevisionCluster,
} from '../../services/revisionClusters';
import type { CustomCalibreEntry } from '../../services/firebase';
import { LineageSuggestionDialog } from './LineageSuggestionDialog';

interface Props {
  manufacturerId: string;
  manufacturerName: string;
  onClose: () => void;
  /** Called after any write, so the Revision lines section reloads. */
  onChanged: () => void;
}

/** Why a cluster's ✨ Ask AI is off, or null when it can run. */
export function askAiBlockedReason(cl: Pick<RevisionCluster, 'members'>): string | null {
  const unlined = cl.members.filter((m) => !m.calibreLineageId).length;
  if (unlined >= 2) return null;
  return 'AI suggestions need at least two calibres that are not already in a revision line. Use ✨ Ask AI on that line instead.';
}

const CREATE_BLOCKED = 'Some of these calibres are already in a revision line. Add the rest from that line\'s Edit dialog.';

const LOOK_LABEL: Record<RevisionCluster['looksLike'], string> = {
  revision: 'looks like revisions',
  tiers: 'suffix is already a tier',
  peers: 'functions differ — peers',
};

export function FindRevisionLinesDialog({ manufacturerId, manufacturerName, onClose, onChanged }: Props) {
  const [calibres, setCalibres] = useState<CustomCalibreEntry[] | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [asking, setAsking] = useState<RevisionCluster | null>(null);

  const load = useCallback(async () => {
    try {
      const [cs, ds] = await Promise.all([
        getCalibresForManufacturer(manufacturerId),
        getDismissedRevisionStems(manufacturerId),
      ]);
      setCalibres(cs);
      setDismissed(new Set(ds));
    } catch (err) {
      console.error('Failed to load calibres for Find revision lines', err);
      toast.error('Could not load calibres');
      setCalibres([]);
    }
  }, [manufacturerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const clusters = useMemo(() => (calibres ? detectRevisionClusters(calibres) : []), [calibres]);
  const folded = useMemo(() => (calibres ? foldedRevisionCodes(calibres) : []), [calibres]);
  const isDismissed = (c: RevisionCluster) => isDismissedCluster(c, dismissed);
  const isHidden = (c: RevisionCluster) => c.looksLike !== 'revision' || isDismissed(c);
  const shown = clusters.filter((c) => showHidden || !isHidden(c));
  const hiddenCount = clusters.filter(isHidden).length;
  const byId = useMemo(() => new Map((calibres ?? []).map((c) => [c.id, c])), [calibres]);

  const createLine = async (cl: RevisionCluster) => {
    setBusy(cl.key);
    try {
      const lineageId = await saveLineage({ baseLabel: cl.stem, manufacturerId });
      for (const m of cl.members) {
        if (m.calibreLineageId) continue; // never moved out of another line
        await setCalibreRevision(m.id, {
          calibreLineageId: lineageId,
          ...(m.suffix ? { revisionLabel: m.suffix } : {}),
        });
      }
      toast.success(`Revision line "${cl.stem}" created`);
      onChanged();
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the line');
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (cl: RevisionCluster) => {
    setBusy(cl.key);
    try {
      await dismissRevisionStem(manufacturerId, cl.key);
      setDismissed(new Set([...dismissed, cl.key]));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Modal isOpen={!asking} onClose={onClose} title={`Find revision lines — ${manufacturerName}`} size="xl">
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            Calibres that share a designation with a revision-like suffix (-1, .2, /1, or a letter after the number —
            Seiko&apos;s Revision Codes: 6R15 → 6R15D). A different number (6R15 vs 6R35) is a different calibre, not a
            revision: group those in a calibre family. Only a suffix that REPLACED the earlier calibre makes a revision line.
          </p>
          {calibres === null ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No candidate revision lines{hiddenCount ? ' (some are hidden)' : ''}.</p>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
              {shown.map((cl) => (
                <li key={cl.key} className="p-3 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-900">
                        {cl.stem}{' '}
                        <Badge variant={cl.looksLike === 'revision' ? 'info' : 'default'}>{LOOK_LABEL[cl.looksLike]}</Badge>
                        {isDismissed(cl) && <Badge variant="default">not a revision</Badge>}
                      </p>
                      <p className="text-xs text-gray-600">
                        {cl.members.map((m) => `${m.name} (${m.refCount})`).join(' · ')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {cl.refTotal} refs
                        {cl.differingSpecs.length ? ` · differs in ${cl.differingSpecs.join(', ')}` : ' · same key specs'}
                        {cl.familyIds.length > 1 ? ` · spans ${cl.familyIds.length} calibre families` : ''}
                        {cl.alreadyLined ? ` · ${cl.alreadyLined} already in a line` : ''}
                      </p>
                      {cl.alreadyLined > 0 && <p className="text-xs text-amber-700">{CREATE_BLOCKED}</p>}
                      {askAiBlockedReason(cl) && <p className="text-xs text-amber-700">{askAiBlockedReason(cl)}</p>}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <span title={cl.alreadyLined > 0 ? CREATE_BLOCKED : undefined}>
                        <Button size="sm" variant="secondary" disabled={!!busy || cl.alreadyLined > 0} loading={busy === cl.key} onClick={() => createLine(cl)}>
                          Create line
                        </Button>
                      </span>
                      <span title={askAiBlockedReason(cl) ?? undefined}>
                        <Button size="sm" variant="secondary" disabled={!!busy || !!askAiBlockedReason(cl)} onClick={() => setAsking(cl)}>
                          ✨ Ask AI
                        </Button>
                      </span>
                      {!isDismissed(cl) && (
                        <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => dismiss(cl)}>
                          Not a revision
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {folded.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1">
              <p className="text-sm font-medium text-amber-900">Revision codes folded into aliases</p>
              <p className="text-xs text-amber-800">
                These revisions exist only as AKAs, so they cannot be grouped yet. Open the calibre and use its Revisions
                card&apos;s &quot;Split folded aliases into separate revisions…&quot;, then come back.
              </p>
              <ul className="text-xs text-amber-900">
                {folded.map((f) => (
                  <li key={f.id}>
                    {f.name}: {f.codes.map((c) => c.alias).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hiddenCount > 0 && (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
              Show {hiddenCount} hidden cluster{hiddenCount === 1 ? '' : 's'} (tiers, peers, or marked not a revision)
            </label>
          )}
        </div>
      </Modal>
      {asking && (
        <LineageSuggestionDialog
          manufacturerId={manufacturerId}
          lineage={null}
          calibreIds={asking.members.map((m) => m.id)}
          seedLabel={asking.stem}
          calibres={asking.members.map((m) => byId.get(m.id)).filter((c): c is CustomCalibreEntry => !!c)}
          onClose={() => {
            setAsking(null);
            void load();
          }}
          onApplied={onChanged}
        />
      )}
    </>
  );
}
